import { useState, useEffect } from 'react'
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

const BADGES = [
  { id: 'first', name: 'Πρώτο Βήμα', emoji: '🌱', desc: '1η ανακύκλωση', threshold: 1 },
  { id: 'regular', name: 'Τακτικός', emoji: '⭐', desc: '10 ανακυκλώσεις', threshold: 10 },
  { id: 'ambassador', name: 'Πρεσβευτής', emoji: '🏆', desc: '25 ανακυκλώσεις', threshold: 25 },
  { id: 'hero', name: 'Eco Hero', emoji: '👑', desc: '50 ανακυκλώσεις', threshold: 50 },
]

export default function DashboardPage() {
  const { profile } = useAuth()
  const [weekData, setWeekData] = useState([])
  const [typeData, setTypeData] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => { fetchStats() }, [profile])

  async function fetchStats() {
    const sevenDaysAgo = new Date()
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

    const { data } = await supabase
      .from('scans')
      .select('scanned_at, bins(type)')
      .eq('user_id', profile?.id)
      .gte('scanned_at', sevenDaysAgo.toISOString())
      .order('scanned_at')

    if (!data) { setLoading(false); return }

    // Εβδομαδιαία δεδομένα
    const days = ['Κυρ', 'Δευ', 'Τρι', 'Τετ', 'Πεμ', 'Παρ', 'Σαβ']
    const dayMap = {}
    for (let i = 6; i >= 0; i--) {
      const d = new Date()
      d.setDate(d.getDate() - i)
      const key = d.toDateString()
      dayMap[key] = { day: days[d.getDay()], count: 0 }
    }
    data.forEach(s => {
      const key = new Date(s.scanned_at).toDateString()
      if (dayMap[key]) dayMap[key].count++
    })
    setWeekData(Object.values(dayMap))

    // Ανά τύπο
    const typeMap = {}
    data.forEach(s => {
      const t = s.bins?.type || 'mixed'
      typeMap[t] = (typeMap[t] || 0) + 1
    })
    const typeLabels = { paper: 'Χαρτί', plastic: 'Πλαστικό', glass: 'Γυαλί', electronics: 'Ηλεκτρονικά', mixed: 'Μεικτό', clothes: 'Ρούχα' }
    setTypeData(Object.entries(typeMap).map(([k, v]) => ({ name: typeLabels[k] || k, count: v })))

    setLoading(false)
  }

  const totalScans = profile?.total_scans || 0
  const points = profile?.points || 0
  const nextBadge = BADGES.find(b => totalScans < b.threshold)
  const progressToNext = nextBadge ? Math.min((totalScans / nextBadge.threshold) * 100, 100) : 100

  return (
    <div style={s.page}>
      {/* Stats */}
      <div style={s.statsGrid}>
        <div style={s.statCard}>
          <div style={s.statLabel}>Πόντοι</div>
          <div style={s.statValue}>{points}</div>
          <div style={s.statSub}>διαθέσιμοι</div>
        </div>
        <div style={s.statCard}>
          <div style={s.statLabel}>Ανακυκλώσεις</div>
          <div style={s.statValue}>{totalScans}</div>
          <div style={s.statSub}>συνολικά</div>
        </div>
        <div style={s.statCard}>
          <div style={s.statLabel}>Αυτή την εβδομάδα</div>
          <div style={s.statValue}>{weekData.reduce((a, d) => a + d.count, 0)}</div>
          <div style={s.statSub}>σκαναρίσματα</div>
        </div>
        <div style={s.statCard}>
          <div style={s.statLabel}>CO₂ που εξοικ.</div>
          <div style={s.statValue}>{(totalScans * 0.3).toFixed(1)}</div>
          <div style={s.statSub}>kg (εκτίμηση)</div>
        </div>
      </div>

      {/* Chart */}
      <div style={s.card}>
        <h3 style={s.cardTitle}>Δραστηριότητα εβδομάδας</h3>
        {loading ? <div style={s.loading}>Φόρτωση...</div> : (
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={weekData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#999' }} />
              <YAxis tick={{ fontSize: 11, fill: '#999' }} allowDecimals={false} />
              <Tooltip formatter={(v) => [`${v} σκαν.`, '']} />
              <Bar dataKey="count" fill="#1A6B3C" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Progress to next badge */}
      {nextBadge && (
        <div style={s.card}>
          <h3 style={s.cardTitle}>Επόμενο badge</h3>
          <div style={s.progressHeader}>
            <span style={s.nextBadgeEmoji}>{nextBadge.emoji}</span>
            <div>
              <div style={s.nextBadgeName}>{nextBadge.name}</div>
              <div style={s.nextBadgeDesc}>{totalScans}/{nextBadge.threshold} ανακυκλώσεις</div>
            </div>
          </div>
          <div style={s.progressBar}>
            <div style={{ ...s.progressFill, width: `${progressToNext}%` }} />
          </div>
        </div>
      )}

      {/* Badges */}
      <div style={s.card}>
        <h3 style={s.cardTitle}>Badges</h3>
        <div style={s.badgeGrid}>
          {BADGES.map(badge => {
            const earned = totalScans >= badge.threshold
            return (
              <div key={badge.id} style={{ ...s.badge, ...(earned ? s.badgeEarned : s.badgeLocked) }}>
                <span style={s.badgeEmoji}>{badge.emoji}</span>
                <div style={s.badgeName}>{badge.name}</div>
                <div style={s.badgeDesc}>{badge.desc}</div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Type breakdown */}
      {typeData.length > 0 && (
        <div style={s.card}>
          <h3 style={s.cardTitle}>Ανά τύπο υλικού</h3>
          {typeData.map(t => (
            <div key={t.name} style={s.typeRow}>
              <span style={s.typeName}>{t.name}</span>
              <div style={s.typeBarWrap}>
                <div style={{ ...s.typeBar, width: `${(t.count / Math.max(...typeData.map(x=>x.count))) * 100}%` }} />
              </div>
              <span style={s.typeCount}>{t.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const s = {
  page: { padding: '1rem', maxWidth: 480, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1rem' },
  statsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' },
  statCard: { background: 'white', borderRadius: 12, padding: '1rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', textAlign: 'center' },
  statLabel: { fontSize: 12, color: '#888', marginBottom: 4 },
  statValue: { fontSize: 28, fontWeight: 700, color: '#1A6B3C' },
  statSub: { fontSize: 11, color: '#aaa', marginTop: 2 },
  card: { background: 'white', borderRadius: 16, padding: '1.25rem', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' },
  cardTitle: { margin: '0 0 14px', fontSize: 15, fontWeight: 600, color: '#333' },
  loading: { textAlign: 'center', color: '#aaa', padding: '2rem 0' },
  progressHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 },
  nextBadgeEmoji: { fontSize: 32 },
  nextBadgeName: { fontWeight: 600, color: '#333', fontSize: 14 },
  nextBadgeDesc: { fontSize: 12, color: '#888' },
  progressBar: { height: 8, background: '#e8f5e9', borderRadius: 4, overflow: 'hidden' },
  progressFill: { height: '100%', background: '#1A6B3C', borderRadius: 4, transition: 'width 0.5s ease' },
  badgeGrid: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 },
  badge: { textAlign: 'center', borderRadius: 10, padding: '10px 6px' },
  badgeEarned: { background: '#f0fdf4' },
  badgeLocked: { background: '#f9f9f9', opacity: 0.45 },
  badgeEmoji: { fontSize: 24, display: 'block', marginBottom: 4 },
  badgeName: { fontSize: 11, fontWeight: 600, color: '#333' },
  badgeDesc: { fontSize: 10, color: '#888', marginTop: 2 },
  typeRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 },
  typeName: { width: 80, fontSize: 13, color: '#555', flexShrink: 0 },
  typeBarWrap: { flex: 1, height: 8, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden' },
  typeBar: { height: '100%', background: '#4CAF50', borderRadius: 4, transition: 'width 0.5s' },
  typeCount: { width: 24, fontSize: 13, fontWeight: 600, color: '#1A6B3C', textAlign: 'right' },
}
